import $ from 'jquery';

$(function () {
    const $app = $('#fop-app');
    if (!$app.length) return;

    // Statutory benchmarks (2025 - 2026)
    const CONSTANTS = {
        minSalary: 8000,
        livingWage: 3028,
        minEsvMonthly: 8000 * 0.22, // 1,760 UAH
        maxEsvMonthly: 15 * 8000 * 0.22, // 26,400 UAH
        limits: {
            group1: 167 * 8000, // 1,336,000 UAH
            group2: 834 * 8000, // 6,672,000 UAH
            group3_5: 1167 * 8000, // 9,336,000 UAH
            group3_3: 1167 * 8000, // 9,336,000 UAH
            general: Infinity
        },
        fixedSingleTaxMonthly: {
            group1: 3028 * 0.10, // 302.80 UAH (10% of living wage)
            group2: 8000 * 0.20 // 1,600.00 UAH (20% of min wage)
        },
        fixedMilitaryMonthly: {
            group1: 8000 * 0.10, // 800 UAH (10% of min wage per Law 4015-IX)
            group2: 8000 * 0.10 // 800 UAH
        }
    };

    // State
    const state = {
        group: 'group3_5',
        currency: 'UAH',
        period: 'month',
        income: 120000,
        fxRateUsd: 41.5,
        fxRateEur: 45.0,
        expenses: 0,
        esvExempt: false,
        displayMode: 'month' // 'month', 'quarter', 'year'
    };

    // Presets
    const presets = {
        it: {
            group: 'group3_5',
            currency: 'USD',
            income: 3000,
            fxRateUsd: 41.5,
            period: 'month',
            expenses: 0,
            esvExempt: false
        },
        agency: {
            group: 'group3_5',
            currency: 'UAH',
            income: 350000,
            period: 'month',
            expenses: 60000,
            esvExempt: false
        },
        local: {
            group: 'group2',
            currency: 'UAH',
            income: 120000,
            period: 'month',
            expenses: 0,
            esvExempt: false
        },
        consultant: {
            group: 'group3_5',
            currency: 'UAH',
            income: 60000,
            period: 'month',
            expenses: 0,
            esvExempt: false
        },
        craft: {
            group: 'group1',
            currency: 'UAH',
            income: 40000,
            period: 'month',
            expenses: 0,
            esvExempt: false
        }
    };

    function formatMoney(num, currency = 'UAH', decimals = 0) {
        if (isNaN(num) || !isFinite(num)) num = 0;
        const formatted = Math.round(num).toLocaleString('uk-UA');
        const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₴';
        return `${formatted} ${symbol}`;
    }

    function formatNumber(num) {
        if (isNaN(num) || !isFinite(num)) num = 0;
        return Math.round(num).toLocaleString('uk-UA');
    }

    function calculate() {
        // Effective exchange rate to UAH
        let fxRate = 1.0;
        if (state.currency === 'USD') fxRate = state.fxRateUsd;
        else if (state.currency === 'EUR') fxRate = state.fxRateEur;

        const incomeUah = state.income * fxRate;
        const expensesUah = state.expenses * fxRate;

        // Normalize to Monthly Gross (UAH)
        let monthlyGross = incomeUah;
        if (state.period === 'quarter') {
            monthlyGross = incomeUah / 3;
        } else if (state.period === 'year') {
            monthlyGross = incomeUah / 12;
        }

        const quarterlyGross = monthlyGross * 3;
        const annualGross = monthlyGross * 12;

        let monthlyExpenses = expensesUah;
        if (state.period === 'quarter') monthlyExpenses = expensesUah / 3;
        else if (state.period === 'year') monthlyExpenses = expensesUah / 12;

        // 1. Single Tax / PIT
        let singleTaxMonthly = 0;
        let singleTaxRateLabel = '';

        if (state.group === 'group1') {
            singleTaxMonthly = CONSTANTS.fixedSingleTaxMonthly.group1;
            singleTaxRateLabel = '10% (302.80 ₴)';
        } else if (state.group === 'group2') {
            singleTaxMonthly = CONSTANTS.fixedSingleTaxMonthly.group2;
            singleTaxRateLabel = '20% (1 600.00 ₴)';
        } else if (state.group === 'group3_5') {
            singleTaxMonthly = monthlyGross * 0.05;
            singleTaxRateLabel = '5%';
        } else if (state.group === 'group3_3') {
            singleTaxMonthly = monthlyGross * 0.03;
            singleTaxRateLabel = '3% (+ 20% VAT)';
        } else if (state.group === 'general') {
            const netProfitMonthly = Math.max(0, monthlyGross - monthlyExpenses);
            singleTaxMonthly = netProfitMonthly * 0.18; // ПДФО 18%
            singleTaxRateLabel = '18% PIT / ПДФО';
        }

        const singleTaxQuarterly = singleTaxMonthly * 3;
        const singleTaxAnnual = singleTaxMonthly * 12;

        // 2. Military Levy (Військовий збір)
        let militaryMonthly = 0;
        let militaryRateLabel = '';

        if (state.group === 'group1' || state.group === 'group2') {
            militaryMonthly = CONSTANTS.fixedMilitaryMonthly[state.group];
            militaryRateLabel = '10% (800 ₴)';
        } else if (state.group === 'group3_5' || state.group === 'group3_3') {
            militaryMonthly = monthlyGross * 0.01;
            militaryRateLabel = '1%';
        } else if (state.group === 'general') {
            const netProfitMonthly = Math.max(0, monthlyGross - monthlyExpenses);
            militaryMonthly = netProfitMonthly * 0.05; // ВЗ 5%
            militaryRateLabel = '5%';
        }

        const militaryQuarterly = militaryMonthly * 3;
        const militaryAnnual = militaryMonthly * 12;

        // 3. ESV (ЄСВ 22%)
        let esvMonthly = 0;
        let esvRateLabel = '';

        if (state.esvExempt) {
            esvMonthly = 0;
            esvRateLabel = '0 ₴';
        } else {
            if (state.group === 'general') {
                const netProfitMonthly = Math.max(0, monthlyGross - monthlyExpenses);
                esvMonthly = Math.min(Math.max(CONSTANTS.minEsvMonthly, netProfitMonthly * 0.22), CONSTANTS.maxEsvMonthly);
                esvRateLabel = '22%';
            } else {
                esvMonthly = CONSTANTS.minEsvMonthly;
                esvRateLabel = '22% (1 760 ₴)';
            }
        }

        const esvQuarterly = esvMonthly * 3;
        const esvAnnual = esvMonthly * 12;

        // Total Taxes
        const totalMonthlyTax = singleTaxMonthly + militaryMonthly + esvMonthly;
        const totalQuarterlyTax = singleTaxQuarterly + militaryQuarterly + esvQuarterly;
        const totalAnnualTax = singleTaxAnnual + militaryAnnual + esvAnnual;

        // Net In-Hand
        const netMonthly = Math.max(0, monthlyGross - totalMonthlyTax);
        const netQuarterly = Math.max(0, quarterlyGross - totalQuarterlyTax);
        const netAnnual = Math.max(0, annualGross - totalAnnualTax);

        // Effective Tax Rate
        const effectiveRate = monthlyGross > 0 ? (totalMonthlyTax / monthlyGross) * 100 : 0;

        // Savings vs Official Employment / General
        // Standard employment burden: PIT 18% + Military 5% + Employer ESV 22% = ~45%
        const employmentTaxAnnual = annualGross * 0.45;
        const annualSavings = Math.max(0, employmentTaxAnnual - totalAnnualTax);

        // Limit Check
        const groupLimit = CONSTANTS.limits[state.group];
        const limitRatio = isFinite(groupLimit) ? Math.min(100, (annualGross / groupLimit) * 100) : 0;
        const isLimitExceeded = isFinite(groupLimit) && annualGross > groupLimit;
        const excessAmount = isLimitExceeded ? (annualGross - groupLimit) : 0;

        return {
            fxRate,
            monthlyGross,
            quarterlyGross,
            annualGross,
            singleTaxMonthly,
            singleTaxQuarterly,
            singleTaxAnnual,
            singleTaxRateLabel,
            militaryMonthly,
            militaryQuarterly,
            militaryAnnual,
            militaryRateLabel,
            esvMonthly,
            esvQuarterly,
            esvAnnual,
            esvRateLabel,
            totalMonthlyTax,
            totalQuarterlyTax,
            totalAnnualTax,
            netMonthly,
            netQuarterly,
            netAnnual,
            effectiveRate,
            annualSavings,
            groupLimit,
            limitRatio,
            isLimitExceeded,
            excessAmount
        };
    }

    function render() {
        const res = calculate();
        const mode = state.displayMode;

        let displayGross = res.monthlyGross;
        let displayNet = res.netMonthly;
        let displayTax = res.totalMonthlyTax;
        let displaySingleTax = res.singleTaxMonthly;
        let displayMilitary = res.militaryMonthly;
        let displayEsv = res.esvMonthly;

        if (mode === 'quarter') {
            displayGross = res.quarterlyGross;
            displayNet = res.netQuarterly;
            displayTax = res.totalQuarterlyTax;
            displaySingleTax = res.singleTaxQuarterly;
            displayMilitary = res.militaryQuarterly;
            displayEsv = res.esvQuarterly;
        } else if (mode === 'year') {
            displayGross = res.annualGross;
            displayNet = res.netAnnual;
            displayTax = res.totalAnnualTax;
            displaySingleTax = res.singleTaxAnnual;
            displayMilitary = res.militaryAnnual;
            displayEsv = res.esvAnnual;
        }

        // Currency converter equivalent note if USD or EUR
        if (state.currency !== 'UAH') {
            const sym = state.currency === 'USD' ? '$' : '€';
            const fxNote = `(${formatNumber(state.income)} ${sym} = ${formatNumber(state.income * res.fxRate)} ₴)`;
            $('#fop-fx-equivalent').text(fxNote).removeClass('d-none');
            $('#fop-fx-rate-wrap').removeClass('d-none');
        } else {
            $('#fop-fx-equivalent').addClass('d-none');
            $('#fop-fx-rate-wrap').addClass('d-none');
        }

        // Show/hide expenses input for General System
        if (state.group === 'general') {
            $('#fop-expenses-wrap').removeClass('d-none');
            $('#fop-tax-single-title').text('18% PIT / ПДФО');
        } else {
            $('#fop-expenses-wrap').addClass('d-none');
            $('#fop-tax-single-title').text($('#fop-tax-single-title').data('orig-text') || 'Єдиний податок');
        }

        // Update KPI Cards
        $('#fop-kpi-net-val').text(formatMoney(displayNet));
        $('#fop-kpi-tax-val').text(formatMoney(displayTax));
        $('#fop-kpi-rate-val').text(`${res.effectiveRate.toFixed(1)}%`);
        $('#fop-kpi-savings-val').text(formatMoney(res.annualSavings));

        // Distribution Bar
        const netPct = displayGross > 0 ? (displayNet / displayGross) * 100 : 100;
        const singleTaxPct = displayGross > 0 ? (displaySingleTax / displayGross) * 100 : 0;
        const milPct = displayGross > 0 ? (displayMilitary / displayGross) * 100 : 0;
        const esvPct = displayGross > 0 ? (displayEsv / displayGross) * 100 : 0;

        $('#fop-bar-net').css('width', `${netPct}%`).attr('aria-valuenow', netPct).text(`${netPct.toFixed(0)}%`);
        $('#fop-bar-single').css('width', `${singleTaxPct}%`).attr('aria-valuenow', singleTaxPct).text(singleTaxPct > 4 ? `${singleTaxPct.toFixed(0)}%` : '');
        $('#fop-bar-mil').css('width', `${milPct}%`).attr('aria-valuenow', milPct).text(milPct > 4 ? `${milPct.toFixed(0)}%` : '');
        $('#fop-bar-esv').css('width', `${esvPct}%`).attr('aria-valuenow', esvPct).text(esvPct > 4 ? `${esvPct.toFixed(0)}%` : '');

        // Badges below distribution
        $('#fop-dist-net-badge').text(formatMoney(displayNet));
        $('#fop-dist-single-badge').text(formatMoney(displaySingleTax));
        $('#fop-dist-mil-badge').text(formatMoney(displayMilitary));
        $('#fop-dist-esv-badge').text(formatMoney(displayEsv));

        // Detailed Table
        $('#fop-tb-gross-m').text(formatMoney(res.monthlyGross));
        $('#fop-tb-gross-q').text(formatMoney(res.quarterlyGross));
        $('#fop-tb-gross-y').text(formatMoney(res.annualGross));

        $('#fop-tb-single-rate').text(res.singleTaxRateLabel);
        $('#fop-tb-single-m').text(formatMoney(res.singleTaxMonthly));
        $('#fop-tb-single-q').text(formatMoney(res.singleTaxQuarterly));
        $('#fop-tb-single-y').text(formatMoney(res.singleTaxAnnual));

        $('#fop-tb-mil-rate').text(res.militaryRateLabel);
        $('#fop-tb-mil-m').text(formatMoney(res.militaryMonthly));
        $('#fop-tb-mil-q').text(formatMoney(res.militaryQuarterly));
        $('#fop-tb-mil-y').text(formatMoney(res.militaryAnnual));

        $('#fop-tb-esv-rate').text(res.esvRateLabel);
        $('#fop-tb-esv-m').text(formatMoney(res.esvMonthly));
        $('#fop-tb-esv-q').text(formatMoney(res.esvQuarterly));
        $('#fop-tb-esv-y').text(formatMoney(res.esvAnnual));

        $('#fop-tb-total-m').text(formatMoney(res.totalMonthlyTax));
        $('#fop-tb-total-q').text(formatMoney(res.totalQuarterlyTax));
        $('#fop-tb-total-y').text(formatMoney(res.totalAnnualTax));

        $('#fop-tb-net-m').text(formatMoney(res.netMonthly));
        $('#fop-tb-net-q').text(formatMoney(res.netQuarterly));
        $('#fop-tb-net-y').text(formatMoney(res.netAnnual));

        // Annual Limit Progress
        if (isFinite(res.groupLimit)) {
            $('#fop-limit-box').removeClass('d-none');
            $('#fop-limit-max-badge').text(`${formatMoney(res.groupLimit)}`);
            $('#fop-limit-used-badge').text(`${res.limitRatio.toFixed(1)}%`);
            $('#fop-limit-progress-bar').css('width', `${Math.min(100, res.limitRatio)}%`);

            if (res.isLimitExceeded) {
                $('#fop-limit-progress-bar').removeClass('bg-success bg-warning').addClass('bg-danger');
                $('#fop-limit-warning').removeClass('d-none');
                $('#fop-limit-excess-val').text(formatMoney(res.excessAmount));
            } else if (res.limitRatio > 80) {
                $('#fop-limit-progress-bar').removeClass('bg-success bg-danger').addClass('bg-warning');
                $('#fop-limit-warning').addClass('d-none');
            } else {
                $('#fop-limit-progress-bar').removeClass('bg-warning bg-danger').addClass('bg-success');
                $('#fop-limit-warning').addClass('d-none');
            }
        } else {
            $('#fop-limit-box').addClass('d-none');
            $('#fop-limit-warning').addClass('d-none');
        }

        // Sync inputs
        $('#fop-income-input').val(state.income);
        $('#fop-income-range').val(state.income);
        $('#fop-fx-input').val(state.currency === 'USD' ? state.fxRateUsd : state.fxRateEur);
    }

    // Helper to sync visual radio selection indicators
    function updateGroupSelectionUI() {
        $('input[name="fop-group"]').each(function () {
            const isChecked = $(this).is(':checked');
            const $card = $(this).closest('label').find('.fop-radio-card');
            $card.toggleClass('active', isChecked);
            $card.find('.fop-radio-checked').toggleClass('d-none', !isChecked);
            $card.find('.fop-radio-unchecked').toggleClass('d-none', isChecked);
        });
    }

    // Event Bindings
    // 1. Group Selector
    $('input[name="fop-group"]').on('change', function () {
        state.group = $(this).val();
        updateGroupSelectionUI();
        render();
    });

    // 2. Currency Selector
    $('button[data-fop-currency]').on('click', function () {
        $('button[data-fop-currency]').removeClass('active btn-primary').addClass('btn-light');
        $(this).addClass('active btn-primary').removeClass('btn-light');
        state.currency = $(this).data('fop-currency');

        // Adjust income step & max for currency
        if (state.currency === 'UAH') {
            $('#fop-income-range').attr({ min: 10000, max: 2000000, step: 5000 });
            $('#fop-currency-sym').text('₴');
            if (state.income < 10000) state.income = 120000;
        } else {
            $('#fop-income-range').attr({ min: 200, max: 50000, step: 100 });
            $('#fop-currency-sym').text(state.currency === 'USD' ? '$' : '€');
            if (state.income > 50000) state.income = 3000;
        }

        render();
    });

    // 3. Period Selector
    $('button[data-fop-period]').on('click', function () {
        $('button[data-fop-period]').removeClass('active btn-primary').addClass('btn-light');
        $(this).addClass('active btn-primary').removeClass('btn-light');
        state.period = $(this).data('fop-period');
        render();
    });

    // 4. Display Mode (Month / Quarter / Year) in Results
    $('button[data-fop-display-mode]').on('click', function () {
        $('button[data-fop-display-mode]').removeClass('active btn-primary').addClass('btn-light text-body');
        $(this).addClass('active btn-primary').removeClass('btn-light text-body');
        state.displayMode = $(this).data('fop-display-mode');
        render();
    });

    // 5. Income Input & Range Sync
    $('#fop-income-input').on('input change', function () {
        const val = parseFloat($(this).val()) || 0;
        state.income = val;
        $('#fop-income-range').val(val);
        render();
    });

    $('#fop-income-range').on('input', function () {
        const val = parseFloat($(this).val()) || 0;
        state.income = val;
        $('#fop-income-input').val(val);
        render();
    });

    // 6. FX Rate Input
    $('#fop-fx-input').on('input change', function () {
        const val = parseFloat($(this).val()) || 1.0;
        if (state.currency === 'USD') state.fxRateUsd = val;
        else if (state.currency === 'EUR') state.fxRateEur = val;
        render();
    });

    // 7. Expenses Input (General System)
    $('#fop-expenses-input').on('input change', function () {
        state.expenses = parseFloat($(this).val()) || 0;
        render();
    });

    // 8. ESV Exemption Checkbox
    $('#fop-esv-exempt').on('change', function () {
        state.esvExempt = $(this).is(':checked');
        render();
    });

    // 9. Presets Buttons
    $('button[data-fop-preset]').on('click', function () {
        const pKey = $(this).data('fop-preset');
        const p = presets[pKey];
        if (!p) return;

        $('button[data-fop-preset]').removeClass('active btn-primary').addClass('btn-light');
        $(this).addClass('active btn-primary').removeClass('btn-light');

        state.group = p.group;
        $(`input[name="fop-group"][value="${p.group}"]`).prop('checked', true);
        updateGroupSelectionUI();

        state.currency = p.currency;
        $('button[data-fop-currency]').removeClass('active btn-primary').addClass('btn-light');
        $(`button[data-fop-currency="${p.currency}"]`).addClass('active btn-primary').removeClass('btn-light');

        if (state.currency === 'UAH') {
            $('#fop-income-range').attr({ min: 10000, max: 2000000, step: 5000 });
            $('#fop-currency-sym').text('₴');
        } else {
            $('#fop-income-range').attr({ min: 200, max: 50000, step: 100 });
            $('#fop-currency-sym').text(state.currency === 'USD' ? '$' : '€');
        }

        state.income = p.income;
        state.period = p.period;
        $('button[data-fop-period]').removeClass('active btn-primary').addClass('btn-light');
        $(`button[data-fop-period="${p.period}"]`).addClass('active btn-primary').removeClass('btn-light');

        state.expenses = p.expenses;
        state.esvExempt = p.esvExempt;
        $('#fop-esv-exempt').prop('checked', p.esvExempt);

        render();
    });

    // Initial render
    updateGroupSelectionUI();
    render();
});

